import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { waReplyText } from "./Conversation";
import { errMsg } from "@/lib/api";
import { maskPhone, waLink } from "@/lib/format";
import { LIB_LABEL } from "@/lib/library";

const Item = ({ row, onDone, fromNumber }) => {
  const { api } = useOutletContext();
  const p = row.participant;
  const [text, setText] = useState(row.suggestion?.text || "");
  const [msg, setMsg] = useState(null);
  const [state, setState] = useState("");
  const prepare = async () => {
    const w = window.open("", "_blank");
    try {
      const { data } = await api.post(`/admin/participants/${p.id}/reply`, { text, channel: "whatsapp", from_number: fromNumber || null });
      setMsg(data);
      if (w) w.location.href = waLink(p.whatsapp, waReplyText(p, data.text));
    } catch (e) { if (w) w.close(); toast.error(errMsg(e)); }
  };
  const mark = async (status) => {
    try { await api.post(`/admin/messages/${msg.id}/whatsapp-status`, { status }); setState(status); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  const LABEL = { enviado: "Envio realizado", nao_enviado: "Não enviado", retornar_depois: "Retornar depois" };
  return (
    <li data-testid={`next-item-${p.id}`} className="space-y-2 py-4">
      <p className="font-semibold">{p.name} <span className="font-normal text-muted-foreground">· {maskPhone(p.whatsapp)} · {p.city}/{p.state}</span></p>
      <p className="truncate text-sm text-muted-foreground">Última mensagem: {p.last_message_preview}</p>
      <p className="text-xs text-[#7A5A14]">{row.suggestion ? `Sugestão: ${row.suggestion.title} (${LIB_LABEL[row.library_category]})` : "Sem resposta pronta para esta categoria — escreva abaixo."}</p>
      <textarea data-testid={`next-text-${p.id}`} value={text} onChange={(e) => setText(e.target.value)} disabled={!!msg} rows={3}
        className="w-full rounded-md border border-zap-line bg-zap-mist px-3 py-2 text-[15px] outline-none focus:border-zap-green disabled:opacity-70" />
      {!msg ? (
        <button data-testid={`next-wa-${p.id}`} onClick={prepare} disabled={!text.trim()} className="flex h-10 items-center gap-2 rounded-md bg-zap-deep px-3 text-sm font-bold text-white disabled:opacity-40"><MessageCircle className="h-4 w-4" /> RESPONDER PELO WHATSAPP</button>
      ) : state ? (
        <p data-testid={`next-state-${p.id}`} className="text-sm font-semibold text-zap-green">{LABEL[state]} — registrado.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button data-testid={`next-sent-${p.id}`} onClick={() => mark("enviado")} className="h-9 rounded-md bg-zap-deep px-3 text-sm font-bold text-white">ENVIO REALIZADO</button>
          <button data-testid={`next-notsent-${p.id}`} onClick={() => mark("nao_enviado")} className="h-9 rounded-md border border-zap-line px-3 text-sm font-bold">NÃO ENVIADO</button>
          <button data-testid={`next-later-${p.id}`} onClick={() => mark("retornar_depois")} className="h-9 rounded-md border border-zap-line px-3 text-sm font-bold">RETORNAR DEPOIS</button>
        </div>
      )}
    </li>
  );
};

export const NextFive = ({ open, onClose, onChanged }) => {
  const { api } = useOutletContext();
  const [rows, setRows] = useState(null);
  const [numbers, setNumbers] = useState([]);
  const [fromNumber, setFromNumber] = useState("");
  useEffect(() => {
    if (open) {
      setRows(null);
      api.get("/admin/queue/next").then((r) => setRows(r.data)).catch(() => setRows([]));
      api.get("/admin/settings").then((r) => { setNumbers(r.data.whatsapp_numbers || []); setFromNumber(r.data.whatsapp_numbers?.[0]?.label || ""); }).catch(() => {});
    }
  }, [open, api]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="next-five-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Próximos cinco atendimentos</DialogTitle>
          <DialogDescription>Revise cada resposta. O envio é individual e manual pelo WhatsApp de cada participante.</DialogDescription>
        </DialogHeader>
        {numbers.length > 0 && (
          <select data-testid="next-from-number" value={fromNumber} onChange={(e) => setFromNumber(e.target.value)} className="h-9 w-fit rounded-md border border-zap-line px-2 text-sm">
            {numbers.map((n) => <option key={n.label} value={n.label}>Enviar de: {n.label}</option>)}
          </select>
        )}
        {rows === null ? <Loader2 className="mx-auto h-6 w-6 animate-spin" /> : rows.length === 0 ? <p data-testid="next-five-empty" className="text-sm text-muted-foreground">Nenhum atendimento pendente.</p> :
          <ul className="divide-y divide-zap-line">{rows.map((r) => <Item key={r.participant.id} row={r} onDone={onChanged} fromNumber={fromNumber} />)}</ul>}
      </DialogContent>
    </Dialog>
  );
};
